import { UserGroup } from '@common/data-models/search-results/result-group';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { GlobalRoles } from '../../utils/util';
import { TeamMembersState } from './team-members.models';

export const selectTeamMembersState = createFeatureSelector<TeamMembersState>('teamMembers');

export const selectTeamMembersLoading = createSelector(selectTeamMembersState, state => state.loading);

export const selectTeamMembersUsers = createSelector(selectTeamMembersState, state => state.users);

export const selectTeamMembersNewUsers = createSelector(selectTeamMembersState, state => state.newUsers);

export const selectTeamMembersSecurity = createSelector(selectTeamMembersState, state => state.security);

export const selectTeamMembersGroupByField = createSelector(selectTeamMembersState, state => state.groupByField);

export const selectTeamMembersSortByField = createSelector(selectTeamMembersState, state => state.sortByField);

export const selectTeamMembersCurrentTabIndex = createSelector(selectTeamMembersState, state => state.currentTabIndex);

export const selectTeamMembersCurrentGroup = createSelector(selectTeamMembersState, state => state.currentGroup);

export const selectTeamMembersCurrentPagePerGroup = createSelector(selectTeamMembersState, state => state.currentPagePerGroup);

export const selectTeamMembersSelectedTeam = createSelector(selectTeamMembersState, state => state.selectedTeam);

export const selectTeamMembersSelectedRole = createSelector(selectTeamMembersState, state => state.selectedRole);

export const selectTeamMembersSelectedStatus = createSelector(selectTeamMembersState, state => state.selectedStatus);

export const selectTeamMembersSearchTerm = createSelector(selectTeamMembersState, state => state.searchTerm);

export const selectAllTeamMembers = createSelector(selectTeamMembersUsers, selectTeamMembersNewUsers, (users, newUsers) => [...users, ...newUsers]);

export const selectTeamMembersUserGroup = createSelector(
    selectTeamMembersUsers,
    selectTeamMembersNewUsers,
    selectTeamMembersGroupByField,
    selectTeamMembersSortByField,
    selectTeamMembersSelectedTeam,
    selectTeamMembersSelectedRole,
    selectTeamMembersSelectedStatus,
    selectTeamMembersSearchTerm,
    (users, newUsers, groupByField, sortByField, selectedTeam, selectedRole, selectedStatus, searchTerm) => {
        const userGroupDefs = UsersResults.userGroupDefs;
        const groupDef = userGroupDefs[groupByField];
        const sortDef = userGroupDefs[sortByField];
        if (!groupDef || !sortDef) return null;

        const userGroup = new UserGroup<UserStaffInfo>();
        for (const user of users) {
            const staff = Object.assign(new UserStaffInfo(), user);
            if (filterTeamMember(staff, selectedTeam, selectedRole, selectedStatus, searchTerm)) userGroup.addToGroups(groupDef.valueConvertor(staff), staff);
        }
        for (const user of newUsers) {
            const staff = Object.assign(new UserStaffInfo(), user);
            if (filterTeamMember(staff, selectedTeam, selectedRole, selectedStatus, searchTerm)) userGroup.addToGroups(groupDef.valueConvertor(staff), staff);
        }
        userGroup.sort(groupDef.groupKeyComparator('asc'), sortDef.userComparator('asc'));
        return userGroup;
    }
);

const filterTeamMember = (user: UserStaffInfo, team?: string, role?: string, status?: number, term?: string) => {
    let teamRes = true;
    let roleRes = true;
    let statusRes = true;
    let termRes = true;
    if (team || role || status !== undefined) {
        if (team) {
            teamRes = user.Program === team;
        }
        if (role) {
            roleRes = role !== GlobalRoles.SUPERVISOR_ID ? user.GlobalRole === role : user.Supervisor;
        }
        if (status !== undefined) {
            statusRes = user.Active !== Boolean(status);
        }
    }

    if (term) {
        termRes = user.GivenName.toString().toLowerCase() === term.toLowerCase() || user.Surname.toString().toLowerCase() === term.toLowerCase();
    }
    return teamRes && roleRes && statusRes && termRes;
};

export const selectTeamMembersGroupKeys = createSelector(selectTeamMembersUserGroup, userGroup => {
    return userGroup?.keys ?? [];
});

export const selectTeamMembersCurrentPage = createSelector(selectTeamMembersCurrentPagePerGroup, selectTeamMembersCurrentGroup, (pagePerGroup, currentGroup) => {
    return pagePerGroup[currentGroup] ?? 0;
});

export const selectTeamMembersTotalItemCount = createSelector(selectTeamMembersUserGroup, selectTeamMembersCurrentGroup, (userGroup, currentGroup) => {
    if (!userGroup || !currentGroup) return 0;
    return userGroup.entries[currentGroup]?.length ?? 0;
});

export const selectTeamMembersCurrentPageItems = createSelector(selectTeamMembersUserGroup, selectTeamMembersCurrentGroup, selectTeamMembersCurrentPage, (userGroup, currentGroup, currentPage) => {
    if (!userGroup || !currentGroup) return [];
    const entries = userGroup.entries[currentGroup];
    if (!entries) return [];
    const start = UserGroup.USERS_PER_PAGE * currentPage;
    const end = UserGroup.USERS_PER_PAGE * (currentPage + 1);
    return entries.slice(start, end);
});

export const selectSecurityRoles = createSelector(selectTeamMembersSecurity, security => {
    if (!security) return [];
    const roles = JSON.parse(JSON.stringify(security));
    roles.unshift(GlobalRoles.SUPERVISOR_ITEM);
    roles.unshift(GlobalRoles.SERVER_MANAGER_ITEM);
    return roles;
});
